<template>
  <div class="dash-collection-block">
    <div class="dash-collection-kpis">
      <div class="dash-collection-kpi">
        <span class="dash-collection-kpi__label">{{ $t("Week total") }}</span>
        <span class="dash-collection-kpi__value">{{
          formatRs(summary.weekTotal)
        }}</span>
      </div>
      <div class="dash-collection-kpi">
        <span class="dash-collection-kpi__label">{{ $t("Daily avg") }}</span>
        <span class="dash-collection-kpi__value">{{
          formatRs(summary.avgDaily)
        }}</span>
      </div>
      <div class="dash-collection-kpi">
        <span class="dash-collection-kpi__label">{{ $t("Today") }}</span>
        <span class="dash-collection-kpi__value">{{
          formatRs(todayCollection)
        }}</span>
      </div>
      <div class="dash-collection-kpi dash-collection-kpi--muted">
        <span class="dash-collection-kpi__label">{{ $t("Receipts") }}</span>
        <span class="dash-collection-kpi__value">{{ todayReceipts }}</span>
      </div>
    </div>

    <b-row class="dash-collection-row match-height">
      <b-col lg="5" md="12" class="dash-collection-col">
        <b-card no-body class="dash-panel dash-collection-panel dash-collection-panel--glass h-100">
          <b-card-header class="dash-panel__header dash-collection-panel__header border-0">
            <div class="dash-panel__heading">
              <span class="dash-panel__icon dash-panel__icon--collection">
                <feather-icon icon="TrendingUpIcon" size="18" />
              </span>
              <div class="dash-panel__heading-main">
                <b-card-title class="dash-panel__title mb-0">{{
                  $t("Weekly Collection")
                }}</b-card-title>
                <small class="dash-panel__subtitle">{{
                  $t("Day-wise breakdown")
                }}</small>
              </div>
            </div>
          </b-card-header>

          <b-card-body class="dash-collection-panel__body">
            <div
              class="dash-table-scroll dash-table-scroll--collection"
              :style="{ minHeight: collectionBodyHeight + 'px' }"
            >
              <b-table
                class="dash-collection-table mb-0"
                :items="summary.rows"
                :fields="tableFields"
                show-empty
                hover
                small
              >
                <template #head()="scope">
                  <span class="dash-collection-th">{{ $t(scope.label) }}</span>
                </template>
                <template #cell(date)="data">
                  <span class="dash-collection-date">{{ data.value }}</span>
                </template>
                <template #cell(collection)="data">
                  <span class="dash-collection-amt">{{
                    formatRs(data.value)
                  }}</span>
                </template>
                <template #cell(share)="data">
                  <div class="dash-collection-share">
                    <div class="dash-collection-share__bar">
                      <span
                        class="dash-collection-share__fill"
                        :style="{ width: Math.min(100, data.value) + '%' }"
                      />
                    </div>
                    <span class="dash-collection-share__pct">{{ data.value }}%</span>
                  </div>
                </template>
                <template #empty>
                  <div class="text-center py-3 text-muted">
                    {{ $t("No records found") }}
                  </div>
                </template>
              </b-table>
            </div>
          </b-card-body>
        </b-card>
      </b-col>

      <b-col lg="7" md="12" class="dash-collection-col">
        <b-card no-body class="dash-panel dash-collection-chart-panel dash-collection-chart-panel--glass h-100">
          <b-card-header class="dash-panel__header dash-collection-chart-panel__header border-0">
            <div class="dash-panel__heading">
              <span class="dash-panel__icon dash-panel__icon--collection-flow">
                <feather-icon icon="ActivityIcon" size="18" />
              </span>
              <div class="dash-panel__heading-main">
                <b-card-title class="dash-panel__title mb-0">{{
                  $t("Collection trend")
                }}</b-card-title>
                <small class="dash-panel__subtitle">{{
                  $t("Last 7 days — area view")
                }}</small>
              </div>
            </div>
          </b-card-header>
          <b-card-body class="dash-collection-chart-panel__body">
            <div
              ref="chartViewport"
              class="dash-collection-chart-viewport"
              :style="{ minHeight: collectionBodyHeight + 'px' }"
            >
              <div v-if="!hasData" class="dash-chart-empty text-muted text-center py-5">
                {{ $t("No records found") }}
              </div>
              <div
                v-else-if="!chartInView"
                class="dash-collection-chart-placeholder"
              />
              <vue-apex-charts
                v-else
                :key="chartRenderKey()"
                type="area"
                :height="collectionBodyHeight"
                :options="chartOptions"
                :series="chartSeries"
                class="dash-collection-apex"
              />
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { BCard, BCardHeader, BCardTitle, BCardBody, BRow, BCol, BTable } from "bootstrap-vue";
import VueApexCharts from "vue-apexcharts";
import chartInViewMixin from "./mixins/chartInViewMixin";
import { dashApexTooltip } from "./utils/dashboardApexTooltip";
import { buildWeeklyCollectionRows } from "./utils/dashboardInsights";

export default {
  name: "CollectionInsightsPanel",
  mixins: [chartInViewMixin],
  components: {
    BCard,
    BCardHeader,
    BCardTitle,
    BCardBody,
    BRow,
    BCol,
    BTable,
    VueApexCharts,
  },
  props: {
    weeklyData: { type: Object, default: () => ({}) },
    todayCollection: { type: [Number, String], default: 0 },
    todayReceipts: { type: [Number, String], default: 0 },
  },
  data() {
    return {
      chartSeries: [{ name: "Collection", data: [] }],
      chartOptions: {},
      tableFields: [
        { key: "date", label: "Date" },
        { key: "collection", label: "Amount" },
        { key: "share", label: "Share" },
      ],
    };
  },
  computed: {
    summary() {
      return buildWeeklyCollectionRows(this.weeklyData);
    },
    hasData() {
      return this.summary.rows && this.summary.rows.length > 0;
    },
    /** Shared body height for table scroll + chart (aligned with FeeBarChart dues logic). */
    collectionBodyHeight() {
      const rows = (this.summary.rows && this.summary.rows.length) || 0;
      if (!rows) return 280;
      return Math.min(520, Math.max(280, rows * 44 + 56));
    },
  },
  watch: {
    weeklyData: {
      deep: true,
      immediate: true,
      handler() {
        this.syncChart();
      },
    },
    chartInView(val) {
      if (val) this.syncChart();
    },
  },
  mounted() {
    this.syncChart();
  },
  methods: {
    formatRs(val) {
      const n = Number(val) || 0;
      return `Rs. ${n.toLocaleString()}`;
    },
    syncChart() {
      const built = buildWeeklyCollectionRows(this.weeklyData);
      const categories = built.rows.map((r) => r.date);
      const values = built.rows.map((r) => r.collection);
      const peak = Math.max(...values, 0);
      const yMax =
        Number(this.weeklyData?.highestCollection) > 0
          ? Number(this.weeklyData.highestCollection)
          : peak > 0
            ? Math.ceil(peak * 1.15)
            : 1000;

      this.chartSeries = [{ name: "Collection", data: values }];
      this.chartOptions = {
        chart: {
          type: "area",
          toolbar: { show: false },
          fontFamily: "inherit",
          background: "transparent",
          animations: this.apexStaggerAnimation(90),
          dropShadow: {
            enabled: true,
            top: 4,
            left: 0,
            blur: 8,
            opacity: 0.12,
            color: "#10b981",
          },
        },
        theme: { mode: "light" },
        stroke: {
          curve: "smooth",
          width: 3,
          colors: ["#7367f0"],
        },
        fill: {
          type: "gradient",
          gradient: {
            shadeIntensity: 0.35,
            opacityFrom: 0.45,
            opacityTo: 0.04,
            stops: [0, 70, 100],
            colorStops: [
              { offset: 0, color: "#9e95f5", opacity: 0.55 },
              { offset: 55, color: "#10b981", opacity: 0.35 },
              { offset: 100, color: "#06b6d4", opacity: 0.03 },
            ],
          },
        },
        colors: ["#7367f0"],
        markers: {
          size: 4,
          strokeWidth: 2,
          strokeColors: "#fff",
          colors: ["#7367f0"],
          hover: { size: 6 },
        },
        dataLabels: { enabled: false },
        grid: {
          borderColor: "rgba(115, 103, 240, 0.08)",
          strokeDashArray: 4,
          padding: { left: 4, right: 8, top: 0, bottom: 0 },
        },
        xaxis: {
          categories,
          labels: {
            style: { colors: "#a8aaae", fontSize: "11px", fontWeight: 600 },
          },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: {
          min: 0,
          max: yMax,
          tickAmount: 5,
          labels: {
            style: { colors: "#a8aaae", fontSize: "11px", fontWeight: 500 },
            formatter(val) {
              if (val >= 1e6) return `${(val / 1e6).toFixed(1)}M`;
              if (val >= 1e3) return `${(val / 1e3).toFixed(0)}k`;
              return Math.round(val);
            },
          },
        },
        tooltip: dashApexTooltip({ shared: false }),
        legend: { show: false },
      };
      this.bumpChartData();
    },
  },
};
</script>

<style scoped lang="scss">
.dash-collection-block {
  margin-top: 0.65rem;
  margin-bottom: 0.35rem;
}

.dash-collection-row {
  align-items: stretch;
  row-gap: 0.85rem;

  &.match-height > [class*="col"] {
    display: flex;
    flex-direction: column;
  }

  &.match-height > [class*="col"] > .card {
    flex: 1 1 auto;
    width: 100%;
  }
}

.dash-collection-col {
  display: flex;
  flex-direction: column;
  margin-bottom: 0;
  min-height: 0;
}

.dash-collection-panel--glass,
.dash-collection-chart-panel--glass {
  border-radius: 22px !important;
  background: rgba(255, 255, 255, 0.62) !important;
  backdrop-filter: blur(16px) saturate(1.12);
  -webkit-backdrop-filter: blur(16px) saturate(1.12);
  border: 1px solid rgba(115, 103, 240, 0.1) !important;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.88) inset,
    0 14px 36px rgba(115, 103, 240, 0.07) !important;
}

.dash-collection-panel__header,
.dash-collection-chart-panel__header {
  padding-bottom: 0.65rem !important;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.55) 0%,
    rgba(255, 255, 255, 0) 100%
  ) !important;
}

.dash-panel__heading-main {
  min-width: 0;
}

.dash-collection-panel,
.dash-collection-chart-panel {
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 300px;
  overflow: hidden;
}

.dash-panel__icon--collection-flow {
  background: linear-gradient(135deg, rgba(237, 231, 255, 0.95) 0%, rgba(236, 253, 245, 0.95) 100%);
  color: #7367f0;
  border: 1px solid rgba(115, 103, 240, 0.12);
}

.dash-collection-kpis {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.55rem;
  padding: 0.75rem 1.1rem;
  margin: 0 0 0.85rem;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.52);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(115, 103, 240, 0.1);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.88) inset,
    0 8px 24px rgba(115, 103, 240, 0.06);
}

@media (max-width: 991px) {
  .dash-collection-kpis {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.dash-collection-kpi {
  padding: 0.5rem 0.55rem;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(16, 185, 129, 0.15);
  box-shadow: 0 4px 14px rgba(16, 185, 129, 0.06);

  &--muted {
    background: rgba(255, 255, 255, 0.55);
    border-color: rgba(115, 103, 240, 0.1);
    box-shadow: 0 4px 12px rgba(115, 103, 240, 0.05);
  }

  &__label {
    display: block;
    font-size: 0.6rem;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #a8aaae;
    margin-bottom: 0.15rem;
  }

  &__value {
    display: block;
    font-size: clamp(0.75rem, 1.8vw, 0.875rem);
    font-weight: 800;
    color: #2f2b3d;
    font-variant-numeric: tabular-nums;
    line-height: 1.25;
    word-break: break-word;
  }

  &--muted .dash-collection-kpi__value {
    color: #7367f0;
  }
}

.dash-collection-panel__body {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 0 0.65rem 0.85rem !important;
}

.dash-table-scroll--collection {
  flex: 1 1 auto;
  min-height: 280px;
  height: 100%;
  overflow: auto;
  overscroll-behavior: contain;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.42);
  border: 1px solid rgba(255, 255, 255, 0.8);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.95);
  padding: 0.15rem 0;
}

.dash-collection-table {
  font-size: 0.8125rem;

  ::v-deep thead th {
    position: sticky;
    top: 0;
    z-index: 2;
    background: rgba(248, 247, 252, 0.95) !important;
    backdrop-filter: blur(8px);
    border-bottom: 1px solid rgba(115, 103, 240, 0.1) !important;
    padding: 0.65rem 0.9rem !important;
  }

  ::v-deep tbody td {
    padding: 0.6rem 0.9rem !important;
    border-color: rgba(235, 233, 241, 0.9) !important;
    vertical-align: middle;
  }

  ::v-deep tbody tr:hover td {
    background: rgba(115, 103, 240, 0.04) !important;
  }
}

.dash-collection-th {
  font-size: 0.625rem;
  font-weight: 800;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #6e6b7b;
}

.dash-collection-date {
  font-weight: 600;
  color: #2f2b3d;
  font-size: 0.8125rem;
}

.dash-collection-amt {
  font-weight: 800;
  color: #28c76f;
  font-variant-numeric: tabular-nums;
  font-size: 0.8125rem;
}

.dash-collection-share {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  min-width: 88px;

  &__bar {
    flex: 1;
    height: 7px;
    border-radius: 999px;
    background: rgba(235, 233, 241, 0.9);
    overflow: hidden;
    box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.04);
  }

  &__fill {
    display: block;
    height: 100%;
    border-radius: 999px;
    background: linear-gradient(90deg, #7367f0, #28c76f);
    transition: width 0.65s cubic-bezier(0.22, 1, 0.36, 1);
  }

  &__pct {
    font-size: 0.6875rem;
    font-weight: 700;
    color: #6e6b7b;
    min-width: 2.35rem;
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
}

.dash-collection-chart-panel__body {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 0 0.75rem 0.85rem !important;
}

.dash-collection-chart-viewport {
  flex: 1 1 auto;
  min-height: 280px;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  padding: 0.35rem 0.25rem 0.15rem;
  background: rgba(255, 255, 255, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.75);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.9);
}

.dash-collection-apex {
  animation: collChartIn 0.55s ease-out both;
}

@keyframes collChartIn {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.dash-collection-chart-placeholder {
  flex: 1;
  min-height: 280px;
  border-radius: 16px;
  background: linear-gradient(
    135deg,
    rgba(115, 103, 240, 0.06) 0%,
    rgba(16, 185, 129, 0.08) 50%,
    transparent 100%
  );
  animation: collPulse 1.5s ease-in-out infinite;
}

.dash-chart-empty {
  font-size: 0.875rem;
  font-weight: 600;
}

@keyframes collPulse {
  0%,
  100% {
    opacity: 0.55;
  }
  50% {
    opacity: 1;
  }
}
</style>
